import React, { useState } from 'react';
import { Lock, Shield, X, Check } from 'lucide-react';
import { InvoiceTemplate } from '../../types';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PRODUCTS, STRIPE_PUBLIC_KEY, getProductForTemplateId } from '../../stripe-config';
import { CURRENCIES } from '../../types';

interface StripePaymentProps {
  template: InvoiceTemplate;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (template: InvoiceTemplate) => void;
}

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

const StripePayment: React.FC<StripePaymentProps> = ({
  template,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'single' | 'monthly' | 'annual'>('single');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Please login to continue');
        setIsProcessing(false);
        return;
      }
      
      // Select the appropriate product based on the selected plan and template price
      let product;
      // Definir o modo de pagamento - usando 'subscription' para todos os casos já que os preços parecem ser recurring
      let paymentMode: 'payment' | 'subscription' = 'subscription';
      
      if (selectedPlan === 'single') {
        // Buscar o produto específico para este template
        const templateProduct = getProductForTemplateId(template.id);
        
        if (templateProduct) {
          product = templateProduct;
          console.log(`Using template-specific product: ${product.name} at ${product.price}`);
        } else {
          // Se não encontrar um produto específico, escolher baseado no preço
          if (template.price <= 2.99) {
            product = STRIPE_PRODUCTS.find(p => p.id === 'prod_single_template');
          } else if (template.price <= 6.99) {
            product = STRIPE_PRODUCTS.find(p => p.id === 'prod_premium_template');
          } else {
            product = STRIPE_PRODUCTS.find(p => p.id === 'prod_exclusive_template');
          }
        }
      } else if (selectedPlan === 'monthly') {
        product = STRIPE_PRODUCTS.find(p => p.id === 'prod_monthly');
      } else if (selectedPlan === 'annual') {
        product = STRIPE_PRODUCTS.find(p => p.id === 'prod_annual');
      }
      
      if (!product) {
        throw new Error('Selected plan not found');
      }
      
      console.log(`Using plan: ${product.name} in mode: ${paymentMode} with currency: ${selectedCurrency}`);
      
      // Construímos o corpo da requisição com base no tipo de produto
      const requestBody: any = {
        mode: paymentMode, // Usar sempre subscription já que os preços parecem ser recurring
        success_url: `${window.location.origin}/template-success?id=${template.id}`,
        cancel_url: `${window.location.origin}/generate-invoice`,
        currency: selectedCurrency.toLowerCase(),
        price_id: product.priceId, // Sempre usar o price_id fixo
        template_id: template.id, // Incluir o ID do template para referência
      };
      
      console.log('Sending checkout request:', requestBody);
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Checkout error response:', data);
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start checkout');
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  // Buscar um produto específico para este template se existir
  const templateProduct = getProductForTemplateId(template.id);
  
  // Se existir um produto específico, usá-lo, caso contrário usar o genérico
  const singleTemplateProduct = templateProduct || 
    { ...STRIPE_PRODUCTS.find(p => p.id === 'prod_single_template'), price: template.price };
    
  const monthlyProduct = STRIPE_PRODUCTS.find(p => p.interval === 'month');
  const annualProduct = STRIPE_PRODUCTS.find(p => p.interval === 'year');

  // Get currency symbol for display
  const currencySymbol = CURRENCIES.find(c => c.code === selectedCurrency)?.symbol || '$';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-gray-900">Choose Your Plan</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Template Info */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 mb-4">
              <h4 className="font-semibold text-gray-900 mb-2">Selected Template: {template.name}</h4>
              <div className="flex gap-2 mb-2">
                {template.colors && Object.values(template.colors).slice(0, 3).map((color, index) => (
                  <div 
                    key={index} 
                    className="w-6 h-6 rounded-full border border-gray-200" 
                    style={{ backgroundColor: color }}
                  ></div>
                ))}
              </div>
              <p className="text-sm text-gray-600">{template.description}</p>
            </div>
            
            <h4 className="font-semibold text-gray-900">Choose your plan:</h4>
            
            {/* Plan Selection */}
            <div className="flex flex-col space-y-4 mt-2">
              {/* Single Template */}
              <div 
                className={`p-4 border rounded-lg cursor-pointer ${selectedPlan === 'single' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'}`}
                onClick={() => setSelectedPlan('single')}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h5 className="font-medium text-gray-900">Unlock This Template</h5>
                    <p className="text-sm text-gray-600 mt-1">{singleTemplateProduct?.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-blue-600">{currencySymbol}{template.price.toFixed(2)}</span>
                    <span className="text-sm text-gray-500"> one-time</span>
                  </div>
                </div>
                
                {template.features && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-1 gap-1">
                      {template.features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center space-x-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span className="text-xs text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Monthly Plan */}
              <div 
                className={`p-4 border rounded-lg cursor-pointer ${selectedPlan === 'monthly' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'}`}
                onClick={() => setSelectedPlan('monthly')}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h5 className="font-medium text-gray-900">Monthly Plan</h5>
                    <p className="text-sm text-gray-600 mt-1">{monthlyProduct?.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-blue-600">{currencySymbol}{monthlyProduct?.price.toFixed(2)}</span>
                    <span className="text-sm text-gray-500">/month</span>
                  </div>
                </div>
                
                {monthlyProduct?.features && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {monthlyProduct.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span className="text-xs text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Annual Plan */}
              <div 
                className={`p-4 border rounded-lg cursor-pointer ${selectedPlan === 'annual' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'}`}
                onClick={() => setSelectedPlan('annual')}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h5 className="font-medium text-gray-900">Annual Plan</h5>
                    <p className="text-sm text-gray-600 mt-1">{annualProduct?.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-blue-600">{currencySymbol}{annualProduct?.price.toFixed(2)}</span>
                    <span className="text-sm text-gray-500">/year</span>
                  </div>
                </div>
                
                {annualProduct?.features && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {annualProduct.features.slice(0, 6).map((feature, index) => (
                        <div key={index} className="flex items-center space-x-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span className="text-xs text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Currency Selection */}
              <div className="mt-6">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                  Select Currency
                </label>
                <select
                  id="currency"
                  className="form-select w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 focus:border-blue-500 focus:ring-blue-500"
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.name} ({currency.code} {currency.symbol})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Choose your preferred currency for payment. Conversion rates will be applied at checkout.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Security Notice */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Shield className="h-4 w-4 text-green-600" />
              <span>Your payment is secured with 256-bit SSL encryption</span>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handlePayment}
            disabled={isProcessing}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isProcessing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Lock className="h-5 w-5" />
                <span>
                  {selectedPlan === 'single' 
                    ? `Unlock for ${currencySymbol}${template.price.toFixed(2)}` 
                    : selectedPlan === 'monthly'
                      ? `Subscribe for ${currencySymbol}${monthlyProduct?.price.toFixed(2)}/month`
                      : `Subscribe for ${currencySymbol}${annualProduct?.price.toFixed(2)}/year`
                  }
                </span>
              </>
            )}
          </button>

          {/* Terms */}
          <p className="mt-4 text-xs text-gray-500 text-center">
            By continuing, you agree to our Terms of Service and Privacy Policy.
            {selectedPlan !== 'single' && ' You can cancel your subscription at any time.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StripePayment;